import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Decimal } from "decimal.js";
import {
  fetchApiGet,
  fetchApiPut,
  fetchApiPost,
  fetchApiDelete,
} from "../../utils";
import { transformToDecimal } from "../../common";
import { BaseDbObject } from "@/lib/models/db";

// Enums for breakout types
export enum BreakoutDimension {
  IRRIGATION = "IRRIGATION",
  GMO = "GMO",
}

export enum BreakoutCompositionType {
  SINGLE = "SINGLE",
  COMPOSITE = "COMPOSITE",
}

export enum IrrigationValue {
  IRRIGATED = "IRRIGATED",
  NON_IRRIGATED = "NON_IRRIGATED",
}

export enum GMOValue {
  GMO = "GMO",
  NON_GMO = "NON_GMO",
}

// Validation schemas
export const BreakoutDimensionValidation = z.enum([
  BreakoutDimension.IRRIGATION,
  BreakoutDimension.GMO,
]);

export const BreakoutCompositionTypeValidation = z.enum([
  BreakoutCompositionType.SINGLE,
  BreakoutCompositionType.COMPOSITE,
]);

export const IrrigationValueValidation = z.enum([
  IrrigationValue.IRRIGATED,
  IrrigationValue.NON_IRRIGATED,
]);

export const GMOValueValidation = z.enum([
  GMOValue.GMO,
  GMOValue.NON_GMO,
]);

export const SingleBreakoutCriteriaValidation = z.object({
  dimension: BreakoutDimensionValidation,
  value: z.string(),
});

export const CompositeBreakoutCriteriaValidation = z.object({
  [BreakoutDimension.IRRIGATION]: IrrigationValueValidation.optional(),
  [BreakoutDimension.GMO]: GMOValueValidation.optional(),
});

// Interfaces
export interface SingleBreakoutCriteria {
  dimension: BreakoutDimension;
  value: string;
}

export interface CompositeBreakoutCriteria {
  [BreakoutDimension.IRRIGATION]?: IrrigationValue;
  [BreakoutDimension.GMO]?: GMOValue;
}

export interface RiskCropProfileBreakout extends BaseDbObject {
  profile_id: string;
  customer_id: string;
  breakout_type: BreakoutCompositionType;
  breakout_value: string; // JSON string of criteria
  acreage: Decimal | null;
  projected_yield: Decimal | null;
  actual_yield: Decimal | null;
  projected_total_production: Decimal | null;
  actual_total_production: Decimal | null;
  cost_per_acre: Decimal | null;
  is_enabled: boolean;
  sort_order: number;
  Profile?: {
    commodity_name: string;
    commodity_type: string;
    crop_year: number;
  };
  Customer?: {
    id: string;
    long_name: string;
  };
}

// Create/Update validations
export const CreateBreakoutValidation = z.object({
  customer_id: z.string().uuid(),
  breakout_type: BreakoutCompositionTypeValidation,
  breakout_criteria: z.union([
    SingleBreakoutCriteriaValidation,
    CompositeBreakoutCriteriaValidation,
  ]),
  acreage: z
    .number()
    .or(z.string())
    .optional()
    .transform(transformToDecimal({ throwOnInvalid: true, key: "acreage" })),
  projected_yield: z
    .number()
    .or(z.string())
    .optional()
    .transform(
      transformToDecimal({ throwOnInvalid: true, key: "projected_yield" })
    ),
  actual_yield: z
    .number()
    .or(z.string())
    .optional()
    .transform(
      transformToDecimal({ throwOnInvalid: true, key: "actual_yield" })
    ),
  cost_per_acre: z
    .number()
    .or(z.string())
    .optional()
    .transform(
      transformToDecimal({ throwOnInvalid: true, key: "cost_per_acre" })
    ),
  is_enabled: z.boolean().optional(),
  sort_order: z.number().optional(),
});

export const UpdateBreakoutValidation = z.object({
  acreage: z
    .number()
    .or(z.string())
    .optional()
    .transform(transformToDecimal({ throwOnInvalid: true, key: "acreage" })),
  projected_yield: z
    .number()
    .or(z.string())
    .optional()
    .transform(
      transformToDecimal({ throwOnInvalid: true, key: "projected_yield" })
    ),
  actual_yield: z
    .number()
    .or(z.string())
    .optional()
    .transform(
      transformToDecimal({ throwOnInvalid: true, key: "actual_yield" })
    ),
  cost_per_acre: z
    .number()
    .or(z.string())
    .optional()
    .transform(
      transformToDecimal({ throwOnInvalid: true, key: "cost_per_acre" })
    ),
  is_enabled: z.boolean().optional(),
  sort_order: z.number().optional(),
});

// Helper functions
export function parseBreakoutValue(
  type: BreakoutCompositionType,
  value: string
): SingleBreakoutCriteria | CompositeBreakoutCriteria {
  const parsed = JSON.parse(value);
  return type === BreakoutCompositionType.SINGLE
    ? (parsed as SingleBreakoutCriteria)
    : (parsed as CompositeBreakoutCriteria);
}

export function getBreakoutLabel(
  type: BreakoutCompositionType,
  value: string
): string {
  const criteria = parseBreakoutValue(type, value);

  if (type === BreakoutCompositionType.SINGLE) {
    const single = criteria as SingleBreakoutCriteria;
    return single.value.replace(/_/g, " ");
  } else {
    const composite = criteria as CompositeBreakoutCriteria;
    const parts: string[] = [];

    if (composite.IRRIGATION) {
      parts.push(composite.IRRIGATION.replace(/_/g, " "));
    }
    if (composite.GMO) {
      parts.push(composite.GMO.replace(/_/g, " "));
    }

    return parts.join(" ");
  }
}

// API Hooks
export function useListBreakouts({
  profileId,
  customerId,
  enabled = true,
}: {
  profileId?: string;
  customerId?: string;
  enabled?: boolean;
}) {
  const queryParams = new URLSearchParams();
  if (profileId) queryParams.set("profile_id", profileId);
  if (customerId) queryParams.set("customer_id", customerId);

  return useQuery({
    queryKey: ["risk", "breakouts", profileId, customerId],
    queryFn: async () => {
      let url = "risk/management/crop_profiles";
      if (profileId) {
        url += `/profile/${profileId}/breakouts`;
      } else if (customerId) {
        url += `/customer/${customerId}/breakouts`;
      } else {
        throw new Error("Either profileId or customerId must be provided");
      }

      const result = await fetchApiGet<{ data: RiskCropProfileBreakout[] }>(
        url,
        queryParams
      );
      return result;
    },
    enabled,
    retry: 2,
  });
}

export function useCreateBreakout(profileId: string) {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreateBreakoutValidation>) => {
    const result = await fetchApiPost<any>(
      `risk/management/crop_profiles/profile/${profileId}/breakouts`,
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk"] });
    }
    return result;
  };
}

export function useUpdateBreakout() {
  const queryClient = useQueryClient();

  return async (
    breakoutId: string,
    data: z.infer<typeof UpdateBreakoutValidation>
  ) => {
    const result = await fetchApiPut<any>(
      `risk/management/crop_profiles/breakouts/${breakoutId}`,
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk"] });
    }
    return result;
  };
}

export function useDeleteBreakout() {
  const queryClient = useQueryClient();

  return async (breakoutId: string) => {
    const result = await fetchApiDelete<any>(
      `risk/management/crop_profiles/breakouts/${breakoutId}`
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk"] });
    }
    return result;
  };
}
